"use client"

import React, { useState, useRef, useEffect } from "react"
import { cn } from "../utils/cn"
import { ChevronDown } from "lucide-react"

const Select = ({ children, value, onValueChange, ...props }) => {
  return React.Children.map(children, (child) => {
    if (child.type === SelectTrigger) {
      return React.cloneElement(child, { value, onValueChange, ...props })
    }
    return child
  })
}

const SelectTrigger = ({ className, children, value, onValueChange, ...props }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [ref])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (child.type === SelectValue) {
            return React.cloneElement(child, { value })
          }
          return child
        })}
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
      {open && (
        <SelectContent
          onValueChange={(newValue) => {
            onValueChange(newValue)
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

const SelectValue = ({ className, value, placeholder, ...props }) => {
  return (
    <span className={cn("block truncate", className)} {...props}>
      {value || placeholder}
    </span>
  )
}

const SelectContent = ({ className, onValueChange, ...props }) => {
  return (
    <div
      className={cn(
        "absolute z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80",
        className,
      )}
      style={{ animation: "fade-in 0.2s ease-out" }}
      {...props}
    >
      <div className="w-full p-1">
        {React.Children.map(props.children, (child) => {
          if (child.type === SelectItem) {
            return React.cloneElement(child, { onSelect: onValueChange })
          }
          return child
        })}
      </div>
    </div>
  )
}

const SelectItem = ({ className, children, value, onSelect, ...props }) => {
  return (
    <div
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      onClick={() => onSelect(value)}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        {/* Checkmark would go here if needed */}
      </span>
      <span className="truncate">{children}</span>
    </div>
  )
}

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem }
